'use client';

import {
  createContext,
  useCallback,
  useMemo,
  useState,
  useContext,
  type ReactNode,
} from 'react';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  ebvGroupingOptionsFromDisplay,
  getEbvConfigGroupedByCategory,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import type { TraitSettingsColumn } from './TraitSettingsModal';
import TraitSettingsModal from './TraitSettingsModal';
import { searchApiRef } from './PublicSearchDataGrid';

type TraitSettingsContextValue = {
  openTraitSettings: (categoryId: string) => void;
};

const TraitSettingsContext = createContext<TraitSettingsContextValue | null>(
  null,
);

export function useTraitSettings() {
  const ctx = useContext(TraitSettingsContext);
  return ctx;
}

type TraitSettingsProviderProps = { children: ReactNode };

export function TraitSettingsProvider({
  children,
}: TraitSettingsProviderProps) {
  const [modalCategory, setModalCategory] = useState<string | null>(null);
  const society = usePublicSearchContext((c) => c.society);
  const {
    columnVisibilityModel,
    setColumnVisibilityModel,
    ebvCategoryVisibility,
    setEbvCategoryVisibility,
    ebvColumnVisibility,
    setEbvColumnVisibility,
    setManualColumnView,
  } = useGridStore();

  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );
  const { useColourSlotGrouping } = useEbvDisplayOptions();
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );

  const grouped = useMemo(
    () => getEbvConfigGroupedByCategory(ebvConfig, ebvGroupingOptions),
    [ebvConfig, ebvGroupingOptions],
  );

  const openTraitSettings = useCallback((categoryId: string) => {
    setModalCategory(categoryId);
  }, []);

  const closeModal = useCallback(() => {
    setModalCategory(null);
  }, []);

  const group = modalCategory
    ? grouped.find((g) => g.category === modalCategory)
    : null;

  const columns: TraitSettingsColumn[] = useMemo(() => {
    if (!group) return [];
    return group.items.map((c) => {
      const n = c.field.replace('ebv', '');
      return {
        field: `EBV${n}`,
        label: (c.label || '').replace(/\^/g, ' '),
      };
    });
  }, [group]);

  const showCategory =
    group && modalCategory
      ? ebvCategoryVisibility[modalCategory] !== false
      : true;

  const columnVisibilityForCategory = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const { field } of columns) {
      // Reflect the persisted per-trait intent, not the live grid model — the
      // latter can be false purely because the EBV display toggles are off.
      out[field] = ebvColumnVisibility[field] !== false;
    }
    return out;
  }, [columns, ebvColumnVisibility]);

  const handleApply = useCallback(
    (showCategoryApplied: boolean, colVis: Record<string, boolean>) => {
      if (!modalCategory || !group) return;

      setEbvCategoryVisibility((prev) => ({
        ...prev,
        [modalCategory]: showCategoryApplied,
      }));

      // Persist per-trait intent so it survives the category-level recompute in
      // SearchResult (and EBV display-toggle changes). Only update when the
      // category is shown — when hidden, keep the prior per-trait choices so
      // re-enabling the category restores them.
      if (showCategoryApplied) {
        setEbvColumnVisibility((prev) => {
          const nextVis = { ...prev };
          for (const { field } of columns) {
            nextVis[field] = colVis[field] !== false;
          }
          return nextVis;
        });
      }

      const nextModel = { ...columnVisibilityModel };
      for (const { field } of columns) {
        nextModel[field] = showCategoryApplied
          ? colVis[field] !== false
          : false;
      }
      setColumnVisibilityModel(nextModel);
      setManualColumnView(true);

      // Relay columns to fit space after visibility change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          searchApiRef.current?.autosizeColumns?.({
            includeHeaders: true,
            includeOutliers: true,
            expand: true,
          });
        });
      });
    },
    [
      modalCategory,
      group,
      columns,
      columnVisibilityModel,
      setEbvCategoryVisibility,
      setEbvColumnVisibility,
      setColumnVisibilityModel,
      setManualColumnView,
    ],
  );

  const value = useMemo(() => ({ openTraitSettings }), [openTraitSettings]);

  return (
    <TraitSettingsContext.Provider value={value}>
      {children}
      {modalCategory && group && (
        <TraitSettingsModal
          open={true}
          onClose={closeModal}
          categoryId={modalCategory}
          categoryName={group.category}
          columns={columns}
          showCategory={showCategory}
          columnVisibility={columnVisibilityForCategory}
          onApply={handleApply}
        />
      )}
    </TraitSettingsContext.Provider>
  );
}
